const moment = require('moment-timezone/builds/moment-timezone-with-data-10-year-range.min');

const LINE_ITEM_SEASONAL_NIGHT = 'line-item/seasonal-night';
const LINE_ITEM_WEEKLY_DISCOUNT = 'line-item/weekly-discount';
const LINE_ITEM_MONTHLY_DISCOUNT = 'line-item/monthly-discount';

const DEFAULT_TIME_ZONE = 'Etc/UTC';

const toFiniteNumber = value => {
  const numberValue = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof numberValue === 'number' && Number.isFinite(numberValue) ? numberValue : null;
};

const normalizePercent = value => {
  const numberValue = toFiniteNumber(value);
  return numberValue == null ? 0 : Math.min(Math.max(numberValue, 0), 99);
};

const normalizeDateString = value => {
  if (!value) {
    return null;
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const normalizeSeasonalRate = (rate, fallbackCurrency) => {
  const startDate = normalizeDateString(rate?.startDate);
  const endDate = normalizeDateString(rate?.endDate);
  const amount = toFiniteNumber(rate?.amount);
  const currency = rate?.currency || fallbackCurrency;

  return startDate && endDate && amount > 0 && currency
    ? {
        id: rate?.id || `${startDate}_${endDate}_${amount}`,
        label: typeof rate?.label === 'string' ? rate.label.trim() : '',
        startDate,
        endDate,
        amount: Math.round(amount),
        currency,
      }
    : null;
};

const normalizeSeasonalRates = (seasonalRates, fallbackCurrency) =>
  Array.isArray(seasonalRates)
    ? seasonalRates
        .map(rate => normalizeSeasonalRate(rate, fallbackCurrency))
        .filter(rate => rate && rate.startDate <= rate.endDate)
    : [];

const getPricingSettings = (publicData = {}, fallbackCurrency) => ({
  weeklyDiscountPercent: normalizePercent(publicData.weeklyDiscountPercent),
  monthlyDiscountPercent: normalizePercent(publicData.monthlyDiscountPercent),
  seasonalRates: normalizeSeasonalRates(publicData.seasonalRates, fallbackCurrency),
});

const rangesOverlap = (rangeA, rangeB) =>
  !!(
    rangeA?.startDate &&
    rangeA?.endDate &&
    rangeB?.startDate &&
    rangeB?.endDate &&
    rangeA.startDate <= rangeB.endDate &&
    rangeB.startDate <= rangeA.endDate
  );

const hasOverlappingSeasonalRate = (rates, candidate, ignoredRateId) =>
  normalizeSeasonalRates(rates, candidate?.currency).some(rate => {
    const isIgnored = ignoredRateId && rate.id === ignoredRateId;
    return !isIgnored && rangesOverlap(rate, candidate);
  });

const getBookingNightStrings = (bookingStart, bookingEnd, timeZone = DEFAULT_TIME_ZONE) => {
  if (!bookingStart || !bookingEnd) {
    return [];
  }

  const start = moment.tz(bookingStart, timeZone).startOf('day');
  const end = moment.tz(bookingEnd, timeZone).startOf('day');

  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
    return [];
  }

  const nights = [];
  const cursor = start.clone();
  while (cursor.isBefore(end)) {
    nights.push(cursor.format('YYYY-MM-DD'));
    cursor.add(1, 'day');
  }
  return nights;
};

const seasonalRateForNight = (night, seasonalRates, currency) =>
  seasonalRates.find(
    rate => rate.currency === currency && rate.startDate <= night && night <= rate.endDate
  );

const groupSeasonalNights = (groups, seasonalRate) => {
  const key = `${seasonalRate.amount}_${seasonalRate.currency}_${seasonalRate.label || ''}`;
  const existing = groups[key] || {
    amount: seasonalRate.amount,
    currency: seasonalRate.currency,
    label: seasonalRate.label,
    nights: 0,
  };
  return {
    ...groups,
    [key]: {
      ...existing,
      nights: existing.nights + 1,
    },
  };
};

const calculateBookingPricing = params => {
  const {
    baseAmount,
    currency,
    bookingStart,
    bookingEnd,
    timeZone = DEFAULT_TIME_ZONE,
    weeklyDiscountPercent = 0,
    monthlyDiscountPercent = 0,
    seasonalRates = [],
  } = params || {};

  const nights = getBookingNightStrings(bookingStart, bookingEnd, timeZone);
  const sanitizedSeasonalRates = normalizeSeasonalRates(seasonalRates, currency);

  const totals = nights.reduce(
    (acc, night) => {
      const seasonalRate = seasonalRateForNight(night, sanitizedSeasonalRates, currency);
      if (seasonalRate) {
        return {
          ...acc,
          seasonalNights: acc.seasonalNights + 1,
          seasonalSubtotal: acc.seasonalSubtotal + seasonalRate.amount,
          seasonalGroups: groupSeasonalNights(acc.seasonalGroups, seasonalRate),
        };
      }
      return {
        ...acc,
        baseNights: acc.baseNights + 1,
        baseSubtotal: acc.baseSubtotal + baseAmount,
      };
    },
    {
      baseNights: 0,
      seasonalNights: 0,
      baseSubtotal: 0,
      seasonalSubtotal: 0,
      seasonalGroups: {},
    }
  );

  const subtotal = totals.baseSubtotal + totals.seasonalSubtotal;
  const normalizedWeeklyDiscount = normalizePercent(weeklyDiscountPercent);
  const normalizedMonthlyDiscount = normalizePercent(monthlyDiscountPercent);
  const discountType =
    nights.length >= 28 && normalizedMonthlyDiscount > 0
      ? 'monthly'
      : nights.length >= 7 && normalizedWeeklyDiscount > 0
      ? 'weekly'
      : null;
  const discountPercent =
    discountType === 'monthly'
      ? normalizedMonthlyDiscount
      : discountType === 'weekly'
      ? normalizedWeeklyDiscount
      : 0;
  const discountAmount = Math.round(subtotal * (discountPercent / 100));

  return {
    currency,
    nightCount: nights.length,
    baseNights: totals.baseNights,
    seasonalNights: totals.seasonalNights,
    baseSubtotal: totals.baseSubtotal,
    seasonalSubtotal: totals.seasonalSubtotal,
    subtotal,
    discountType,
    discountPercent,
    discountAmount,
    discountedSubtotal: subtotal - discountAmount,
    seasonalGroups: Object.values(totals.seasonalGroups),
  };
};

module.exports = {
  LINE_ITEM_SEASONAL_NIGHT,
  LINE_ITEM_WEEKLY_DISCOUNT,
  LINE_ITEM_MONTHLY_DISCOUNT,
  calculateBookingPricing,
  getBookingNightStrings,
  getPricingSettings,
  hasOverlappingSeasonalRate,
  normalizeSeasonalRate,
  normalizeSeasonalRates,
  normalizePercent,
  rangesOverlap,
};
